// ============================================================================
// Return / Damage items — one modal, one submit path, two destinations.
//
// The Mode toggle at the top asks the only question that matters here: do the
// goods come back (Return) or does the customer keep them (Damage). Everything
// else — qty ceilings, shared fields below the table, which backend endpoint
// each row posts to — is derived from that one choice.
//
// Previously this was the "restock on return" checkbox. That flag is now
// deprecated: the universal-CN flow does not support `restock=false` (goods are
// always picked up when a RETURN escalation runs), and the "customer keeps the
// goods" case has its own clean flow — DAMAGE_REFUND — which also now carries
// the SO linkage so Zoho moves quantity_cancelled on the right SO line.
// ============================================================================
import {
  Alert, Form, Input, InputNumber, Modal, Radio, Select, Space, Table,
  Typography, message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getRefundableLines, returnItems } from "../../oms.api";
import { WAREHOUSES } from "../../../../interfaces/warehouse.interface";
import {
  DamageContextResponse, FileDamageInput, fileDamageRefund, getDamageContext,
} from "../../damage/damage.api";
import {
  UserBankDetails, addUserBankDetails, isPickupDayDisabled, listUserBankDetails,
} from "../returns.api";
import { RefundDestinationPicker } from "../../_shared/RefundDestinationPicker";
import type {
  DestinationBlock,
} from "../../_shared/RefundDestinationPicker/types";
import { usePermissions } from "../../../../custom_hooks/permission/permission_hook";

export type ReturnDamageMode = "return" | "damage";

// The minimum the dialog needs to identify + price a line. Both qtys are optional for back-compat
// with older callers, but at least one must be > 0 for the row to render actionably.
export interface ReturnableLine {
  id: number;
  product_name: string;
  returnable_qty: string;          // ceiling in Return mode
  damageable_qty?: string;         // ceiling in Damage mode
  unit_selling_price: string;      // display-only rate per unit (from the estimate line)
  unit_price_incl_tax?: string;
  unit: string | null;
  ordered_qty?: string;
  delivered_qty?: string;
  returned_qty?: string;
}

interface Props {
  open: boolean;
  salesOrderId?: number;
  lines: ReturnableLine[];
  // Customer's user id — the Damage-mode "Customer bank" destination needs it to list / add
  // UserBankDetails. Omit = only Wallet is usable on the Damage side.
  userId?: string | null;
  onClose: () => void;
  // Called once after a submit where at least one line's call succeeded, so the parent can refetch
  // the SO snapshot. Not called on an all-failed submit — the modal stays open with its errors in
  // place so the operator can retry.
  onDone: () => void;
}

interface Draft {
  picked: boolean;
  qty?: number;
  rate?: number;
}

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 2,
});

// Mode-aware ceiling for one line. Returnable in return-mode, damageable in damage-mode. 0 when
// the line has nothing available on that side — the row still renders but its inputs clamp to 0,
// which keeps the layout stable when the operator flips the mode.
const ceilingFor = (line: ReturnableLine, mode: ReturnDamageMode): number => {
  const n = Number(mode === "return" ? line.returnable_qty : (line.damageable_qty ?? "0"));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

const BatchReturnDamageModal: React.FC<Props> = ({
  open, salesOrderId, lines, userId, onClose, onDone,
}) => {
  const [mode, setMode] = useState<ReturnDamageMode>("return");
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});

  // Return-mode-only shared fields.
  const [pickupDate, setPickupDate] = useState<Dayjs | null>(null);
  const [returnWhId, setReturnWhId] = useState<number | undefined>();
  const [loadingRequired, setLoadingRequired] = useState<boolean | null>(null);
  const [liftAvailable, setLiftAvailable] = useState<boolean | null>(null);
  const [floorNo, setFloorNo] = useState<number | null>(null);

  // Destination + bank live in the unified `RefundDestinationPicker` (SPEC §5.1). The picker
  // emits a `DestinationBlock` + a validity flag; the submit path translates them into the API
  // payload. Return raise defaults to "Not decided yet" — wallet/customer files the CN at raise,
  // so that has to be an explicit pick. Damage (always a money commitment) defaults to wallet.
  const [destBlock, setDestBlock] = useState<DestinationBlock>({ destination: "dont_know" });
  const [isDestValid, setIsDestValid] = useState(false);
  const [banks, setBanks] = useState<UserBankDetails[]>([]);

  // Admin-only "Pre-authorize refund now?" toggle, re-added after it was dropped on
  // feature/refund-destination-at-raise. The toggle is purely a UX guard: when ON, the picker's
  // "Not decided yet" card is hidden so an admin can't accidentally raise a plain return when
  // they meant to commit money. When OFF (default), the picker shows all three cards exactly as
  // non-admins see it. The destination enum itself still drives back-end behaviour — picking
  // wallet / customer in the picker files the CN at raise regardless of the toggle's position,
  // so an admin who leaves the toggle on No and clicks Wallet still pre-authorises. The toggle
  // is only visible to holders of the `refund.before_return` slug.
  const { hasPermission } = usePermissions();
  const canPreAuthorize = hasPermission("refund.before_return");
  const [preAuthorize, setPreAuthorize] = useState(false);

  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const editedRates = useRef<Set<number>>(new Set());

  // On every open, prime per-line drafts: pick every row, qty = full ceiling for the current mode,
  // rate = estimate's unit_selling_price. Flipping the mode after that re-clamps qty against the
  // new ceiling (see the second effect).
  useEffect(() => {
    if (!open) return;
    const seed: Record<number, Draft> = {};
    for (const l of lines) {
      seed[l.id] = {
        picked: true,
        qty: ceilingFor(l, mode),
        rate: Math.round((Number(l.unit_price_incl_tax) || 0) * 100) / 100,
      };
    }
    setDrafts(seed);
    editedRates.current = new Set();
    if (salesOrderId != null) {
      getRefundableLines(salesOrderId, true).then(({ rows }) => {
        setDrafts((prev) => {
          const next = { ...prev };
          for (const r of rows) {
            const p = Number(r.unit_refund_price);
            if (next[r.line_id] && !editedRates.current.has(r.line_id) && p > 0) {
              next[r.line_id] = { ...next[r.line_id], rate: p };
            }
          }
          return next;
        });
      });
    }
    setPickupDate(null);
    setReturnWhId(undefined);
    setLoadingRequired(null);
    setLiftAvailable(null);
    setFloorNo(null);
    setDestBlock({ destination: "dont_know" });
    setIsDestValid(false);
    setPreAuthorize(false);
    setReason("");
    setMode("return");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lines]);

  // Mode-flip re-clamp: qty > the new ceiling is pulled down to it. Keeps the summary total honest
  // when flipping between Return (larger qty) and Damage (smaller qty, since the pool has usually
  // been partly consumed by prior returns).
  useEffect(() => {
    setDrafts((prev) => {
      const next: Record<number, Draft> = {};
      for (const l of lines) {
        const prevRow = prev[l.id] ?? { picked: true, qty: 0, rate: 0 };
        const cap = ceilingFor(l, mode);
        next[l.id] = {
          ...prevRow,
          qty: Math.min(Number(prevRow.qty) || 0, cap),
        };
      }
      return next;
    });
  }, [mode, lines]);

  // Load the customer's bank accounts whenever the modal has a userId. The picker renders them
  // when the operator picks `customer`; cheap one-shot fetch, no need to defer.
  useEffect(() => {
    if (open && userId) {
      listUserBankDetails(userId).then(setBanks);
    }
  }, [open, userId]);

  const setDraft = (id: number, patch: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const totalRupees = useMemo(() => {
    let t = 0;
    for (const l of lines) {
      const d = drafts[l.id];
      if (!d?.picked) continue;
      const q = Number(d.qty) || 0;
      const r = Number(d.rate) || 0;
      if (q > 0) t += q * r;
    }
    return t;
  }, [lines, drafts]);

  const anyPickedWithQty = useMemo(
    () => lines.some((l) => drafts[l.id]?.picked && Number(drafts[l.id]?.qty) > 0),
    [lines, drafts]
  );

  const loadingAnswered =
    loadingRequired === false || (loadingRequired === true && liftAvailable !== null && floorNo !== null);

  // The picker is the sole authority on destination validity. In Return mode a `dont_know` block
  // is always valid (plain raise, no money yet). In Damage mode `dont_know` can't be sent — the
  // damage flow IS the money commitment. Operator-level feedback is in the picker display
  // (headroom overrun → red + isValid=false).
  const destOk =
    mode === "return"
      ? destBlock.destination === "dont_know" || isDestValid
      : destBlock.destination !== "dont_know" && isDestValid;

  const canSubmit = anyPickedWithQty && totalRupees > 0 && destOk && (
    mode === "return" ? !!pickupDate && loadingAnswered : true
  );

  const handleSubmit = async () => {
    if (!canSubmit) {
      if (mode === "return" && !pickupDate) {
        message.error("Pick a return pickup date.");
      } else if (mode === "return" && !loadingAnswered) {
        message.error("Say whether loading is needed, and if so the floor and lift.");
      } else if (!destOk) {
        message.error("Pick a destination for the refund (or 'don't know' to decide at Authorize).");
      } else {
        message.error("Pick at least one line with qty > 0 and a non-zero total.");
      }
      return;
    }
    // An inline-added account is saved once up front so every line (and the damage endpoint,
    // which has no destination_block support) refunds against the same row.
    let bankId: number | null =
      destBlock.destination === "customer" ? destBlock.bank_details_id ?? null : null;
    if (destBlock.destination === "customer" && destBlock.new_bank_details) {
      if (!userId) {
        message.error("Customer id missing — cannot save the bank account.");
        return;
      }
      setSubmitting(true);
      const row = await addUserBankDetails(userId, destBlock.new_bank_details);
      if (!row) {
        setSubmitting(false);
        return;
      }
      setBanks((prev) => [row, ...prev.filter((b) => b.id !== row.id)]);
      bankId = row.id;
    }
    setSubmitting(true);
    const picked = lines.filter((l) => drafts[l.id]?.picked && Number(drafts[l.id]?.qty) > 0);
    const results: { line: ReturnableLine; ok: boolean }[] = [];
    for (const l of picked) {
      const d = drafts[l.id]!;
      try {
        if (mode === "return") {
          // SPEC §4.1: on return raise, when destination is wallet/customer, build the legacy
          // `authorize_now` dict for the backend (translated to destination_block internally).
          // dont_know → skip authorize_now entirely; raise proceeds plain, Authorize later owns
          // the money side (Branch C).
          let authorizeNow: Parameters<typeof returnItems>[1]["authorize_now"];
          if (destBlock.destination !== "dont_know") {
            const ctx = await getDamageContext(l.id);
            const items = buildDamageItems(ctx, Number(d.qty) || 0, Number(d.rate) || 0)
              .map((it) => ({ ...it, product_name: l.product_name }));
            if (items.length === 0) {
              results.push({ line: l, ok: false });
              continue;
            }
            authorizeNow = {
              items,
              destination: destBlock.destination,
              bank_details_id: bankId,
              note: reason,
            };
          }
          const ok = await returnItems(l.id, {
            qty: Number(d.qty),
            // restock is deprecated — the universal-CN flow only supports goods coming back; the
            // "customer keeps the goods" case is Damage (file_damage_refund), which this modal
            // now routes to directly via the Mode toggle.
            restock: true,
            return_wh_id: returnWhId,
            reason,
            pickup_date: pickupDate!.format("YYYY-MM-DD"),
            authorize_now: authorizeNow,
            loading: loadingRequired
              ? { required: true, lift_available: !!liftAvailable, floor_no: floorNo ?? 0 }
              : { required: false },
          });
          results.push({ line: l, ok: !!ok });
        } else {
          // Damage: file_damage_refund wants per-allocation-item rows. Fetch the live context so
          // we distribute the operator-entered qty across this SOL's AllocationItems FIFO (common
          // case is a single AI per SOL — this handles split lines without the FE having to know
          // the breakdown up front).
          const ctx = await getDamageContext(l.id);
          const items = buildDamageItems(ctx, Number(d.qty) || 0, Number(d.rate) || 0);
          if (items.length === 0) {
            results.push({ line: l, ok: false });
            continue;
          }
          // Damage mode never carries dont_know (gated out via `destOk`).
          const input: FileDamageInput = {
            items,
            destination: destBlock.destination === "customer" ? "customer" : "wallet",
            bank_details_id: bankId,
            note: reason,
            idempotency_key: `${l.id}-${Date.now()}`,
          };
          const resp = await fileDamageRefund(l.id, input);
          results.push({ line: l, ok: !!resp });
        }
      } catch {
        results.push({ line: l, ok: false });
      }
    }
    setSubmitting(false);
    const okCount = results.filter((r) => r.ok).length;
    const failed = results.filter((r) => !r.ok);
    if (okCount > 0) {
      const verb = mode === "return" ? "Return raised" : "Damage filed";
      message.success(
        failed.length
          ? `${verb} on ${okCount} of ${results.length} line(s). ${failed.length} failed — modal kept open.`
          : `${verb} on ${okCount} line(s).`
      );
      onDone();
      if (!failed.length) onClose();
    } else {
      message.error("No lines succeeded — check the per-line errors.");
    }
  };

  const columns: ColumnsType<ReturnableLine> = [
    {
      title: "",
      key: "pick",
      width: 40,
      render: (_: unknown, r) => {
        const cap = ceilingFor(r, mode);
        return (
          <input
            type="checkbox"
            checked={!!drafts[r.id]?.picked}
            disabled={cap <= 0}
            onChange={(e) => setDraft(r.id, { picked: e.target.checked })}
          />
        );
      },
    },
    {
      title: "Item",
      dataIndex: "product_name",
      key: "product_name",
      render: (v: string, r) => {
        const cap = ceilingFor(r, mode);
        return (
          <div>
            <Typography.Text>{v}</Typography.Text>
            <br />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {mode === "return" ? "returnable" : "damageable"} {cap} {r.unit ?? ""}
            </Typography.Text>
          </div>
        );
      },
    },
    {
      title: "Ordered",
      key: "ordered_qty",
      width: 80,
      align: "right",
      render: (_: unknown, r) => (
        <Typography.Text type="secondary">{r.ordered_qty ?? "—"}</Typography.Text>
      ),
    },
    {
      title: "Delivered",
      key: "delivered_qty",
      width: 90,
      align: "right",
      render: (_: unknown, r) => (
        <Typography.Text type="secondary">{r.delivered_qty ?? "—"}</Typography.Text>
      ),
    },
    {
      title: "Already returned",
      key: "returned_qty",
      width: 120,
      align: "right",
      render: (_: unknown, r) => (
        <Typography.Text type="secondary">{r.returned_qty ?? "0"}</Typography.Text>
      ),
    },
    {
      title: "Qty",
      key: "qty",
      width: 110,
      render: (_: unknown, r) => {
        const cap = ceilingFor(r, mode);
        return (
          <InputNumber
            min={0}
            max={cap}
            step={1}
            value={drafts[r.id]?.qty}
            disabled={!drafts[r.id]?.picked || cap <= 0}
            onChange={(v) => setDraft(r.id, { qty: Number(v) || 0 })}
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: "Rate incl. GST (₹)",
      key: "rate",
      width: 130,
      render: (_: unknown, r) => (
        <InputNumber
          min={0}
          step={0.01}
          precision={2}
          value={drafts[r.id]?.rate}
          disabled={!drafts[r.id]?.picked}
          onChange={(v) => {
            editedRates.current.add(r.id);
            setDraft(r.id, { rate: Number(v) || 0 });
          }}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Total",
      key: "total",
      width: 120,
      align: "right",
      render: (_: unknown, r) => {
        const d = drafts[r.id];
        const t = d?.picked ? (Number(d.qty) || 0) * (Number(d.rate) || 0) : 0;
        return <Typography.Text strong>{inr.format(t)}</Typography.Text>;
      },
    },
  ];

  return (
    <>
      <Modal
        open={open}
        title={`Return / Damage — ${lines.length} line${lines.length === 1 ? "" : "s"} available`}
        onCancel={submitting ? undefined : onClose}
        onOk={handleSubmit}
        okText={submitting ? "Filing…" : (mode === "return" ? "Raise return" : "File damage")}
        okButtonProps={{ disabled: !canSubmit, loading: submitting }}
        width={960}
        destroyOnClose
      >
        <Form layout="vertical" style={{ marginBottom: 12 }}>
          <Form.Item
            label="What's happening with these items?"
            tooltip="Return — goods come back to a warehouse via carrier pickup; a credit note is filed on authorize. Damage — customer keeps the goods; a credit note is filed straight away (money only, no stock)."
            style={{ marginBottom: 0 }}
          >
            <Radio.Group value={mode} onChange={(e) => {
              const next = e.target.value as ReturnDamageMode;
              setMode(next);
              setDestBlock({ destination: next === "return" ? "dont_know" : "wallet" });
              setIsDestValid(false);
            }}>
              <Radio.Button value="return">Return (goods come back)</Radio.Button>
              <Radio.Button value="damage">Damage (customer keeps goods)</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={mode === "return"
            ? (destBlock.destination !== "dont_know"
                ? "A destination is picked — the rate on each row is final (lands on the CN filed at raise)."
                : "Rate here is informational — the final refund figure is set when you authorize the return after pickup + QC.")
            : "Damage files the credit note immediately. The rate here is the per-unit figure including GST."}
        />

        <Table<ReturnableLine>
          size="small"
          pagination={false}
          rowKey="id"
          dataSource={lines}
          columns={columns}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7}>
                <Typography.Text strong>
                  {mode === "return" ? "Return total (informational)" : "Damage refund total"}
                </Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text strong style={{ fontSize: 15 }}>
                  {inr.format(totalRupees)}
                </Typography.Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />

        <Form layout="vertical" style={{ marginTop: 16 }}>
          {mode === "return" ? (
            <>
              <Space size="middle" wrap>
                <Form.Item
                  label="Return pickup date"
                  required
                  tooltip="The day the carrier should collect the returned goods from the customer."
                >
                  <DatePicker
                    value={pickupDate}
                    onChange={setPickupDate}
                    format="YYYY-MM-DD"
                    disabledDate={(d: Dayjs) => isPickupDayDisabled(d, false)}
                    style={{ width: 200 }}
                  />
                </Form.Item>
                <Form.Item
                  label="Return warehouse"
                  tooltip="Where the returned goods physically land. Leave blank to default to where the order shipped from."
                >
                  <Select
                    value={returnWhId}
                    onChange={setReturnWhId}
                    allowClear
                    showSearch
                    style={{ width: 240 }}
                    placeholder="Default (ship origin)"
                    options={WAREHOUSES.map((w: { id: number; label: string }) => ({
                      value: w.id, label: w.label,
                    }))}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Space>

              <Space size="middle" wrap align="start">
                <Form.Item
                  label="Loading needed?"
                  required
                  tooltip="Should our team load the goods at the customer's site? Tells the pickup crew what labour to send."
                >
                  <Radio.Group
                    value={loadingRequired}
                    onChange={(e) => setLoadingRequired(e.target.value)}
                  >
                    <Radio.Button value={true}>Yes</Radio.Button>
                    <Radio.Button value={false}>No</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                {loadingRequired && (
                  <>
                    <Form.Item label="Lift available?" required>
                      <Radio.Group
                        value={liftAvailable}
                        onChange={(e) => setLiftAvailable(e.target.value)}
                      >
                        <Radio.Button value={true}>Yes</Radio.Button>
                        <Radio.Button value={false}>No</Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                    <Form.Item label="Floor number" required tooltip="0 = ground floor, negative = basement.">
                      <InputNumber
                        min={-5}
                        max={200}
                        precision={0}
                        value={floorNo}
                        onChange={(v) => setFloorNo(v === null ? null : Number(v))}
                        placeholder="e.g. 3"
                        style={{ width: 120 }}
                      />
                    </Form.Item>
                  </>
                )}
              </Space>

              {canPreAuthorize && (
                <Form.Item
                  label="Pre-authorize refund now?"
                  tooltip="Admin-only. When ON, the 'Not decided yet' destination is hidden — pick wallet or customer bank below and the credit note is filed at raise. When OFF, the picker shows all three options, including defer-to-Authorize."
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group
                    value={preAuthorize}
                    onChange={(e) => setPreAuthorize(!!e.target.value)}
                  >
                    <Radio.Button value={false}>No — authorize after QC</Radio.Button>
                    <Radio.Button value={true}>Yes — authorize now</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              )}

              {salesOrderId != null && (
                <>
                  {destBlock.destination !== "dont_know" && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="The rate on each row above is the refund figure that lands on the CN. Double-check per-unit rate before raising — editing is harder once the CN is filed."
                    />
                  )}
                  <RefundDestinationPicker
                    mount="raise"
                    salesOrderId={salesOrderId}
                    refundAmountPaise={Math.round(totalRupees * 100)}
                    userId={userId ?? null}
                    savedBankDetails={banks}
                    initial={destBlock}
                    hideDontKnow={preAuthorize}
                    onChange={(block, valid) => {
                      setDestBlock(block);
                      setIsDestValid(valid);
                    }}
                  />
                </>
              )}
            </>
          ) : (
            salesOrderId != null && (
              <RefundDestinationPicker
                // Damage mode files the money immediately — SPEC §5.1 says the picker mounted at
                // Authorize hides dont_know, which is the same restriction we need here.
                mount="authorize"
                salesOrderId={salesOrderId}
                refundAmountPaise={Math.round(totalRupees * 100)}
                userId={userId ?? null}
                savedBankDetails={banks}
                initial={destBlock}
                onChange={(block, valid) => {
                  setDestBlock(block);
                  setIsDestValid(valid);
                }}
              />
            )
          )}
          <Form.Item label="Reason" style={{ marginBottom: 0 }}>
            <Input.TextArea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Short context for the audit trail (optional)."
            />
          </Form.Item>
        </Form>
      </Modal>

    </>
  );
};

// Distribute operator-entered qty across a SOL's AllocationItems FIFO, bounded by each AI's own
// damageable_qty. Common case: a single AI per SOL, so this is a one-element array; split lines
// consume FIFO. If no AI has damageable qty > 0 the result is empty — the row is skipped (the
// backend would refuse anyway).
function buildDamageItems(
  ctx: DamageContextResponse | undefined,
  qty: number,
  rate: number,
): { allocation_item_id: number; qty: number; rate_incl_tax: number }[] {
  if (!ctx || !ctx.items.length || qty <= 0) return [];
  const out: { allocation_item_id: number; qty: number; rate_incl_tax: number }[] = [];
  let remaining = qty;
  for (const ai of ctx.items) {
    if (remaining <= 0) break;
    const cap = Number(ai.damageable_qty) || 0;
    if (cap <= 0) continue;
    const take = Math.min(remaining, cap);
    out.push({ allocation_item_id: ai.allocation_item_id, qty: take, rate_incl_tax: rate });
    remaining -= take;
  }
  return out;
}

export default BatchReturnDamageModal;
